import { useState, useEffect, useCallback } from 'react';
import { StoryboardMetadata, HoverPreviewState } from '../../types/player';
import { isSafeUrl, sanitizeUrl } from '../../utils/url-utils';

const initialHoverPreview: HoverPreviewState = {
  visible: false,
  x: 0,
  backgroundPosition: '0px 0px',
  width: 0,
  height: 0,
  hoverTime: 0,
  isAtLeftEdge: false,
  isAtRightEdge: false,
};

export const useStoryboardPreview = (duration: number) => {
  const [storyboard, setStoryboard] = useState<StoryboardMetadata | null>(null);
  const [hoverPreview, setHoverPreview] = useState<HoverPreviewState>(initialHoverPreview);

  // Fetch storyboard metadata
  const fetchStoryboardMetadata = useCallback(
    async (url: string) => {
      if (!url) return;

      // Validate URL
      if (!isSafeUrl(url)) {
        console.error('Invalid or unsafe storyboard metadata URL:', url);
        return;
      }

      // Sanitize URL
      const sanitizedUrl = sanitizeUrl(url);
      if (!sanitizedUrl) {
        console.error('Failed to sanitize storyboard URL');
        return;
      }

      // Check if we already have this storyboard
      if (storyboard?.url === sanitizedUrl) {
        console.log('Storyboard metadata already loaded');
        return;
      }

      // Fetch the storyboard data
      try {
        const response = await fetch(sanitizedUrl);

        if (!response.ok) {
          throw new Error(
            `Failed to fetch storyboard metadata: ${response.status} ${response.statusText}`,
          );
        }

        const data: StoryboardMetadata = await response.json();

        // Validate the data
        if (!data.url || !data.tiles || !Array.isArray(data.tiles) || data.tiles.length === 0) {
          console.error('Invalid storyboard metadata:', data);
          return;
        }

        setStoryboard(data);
        console.log('Storyboard metadata loaded successfully');
      } catch (err) {
        console.error('Failed to load storyboard metadata:', err);
      }
    },
    [storyboard],
  );

  // Calculate hover preview data for timeline
  const calculateHoverPreview = useCallback(
    (
      progressRect: DOMRect,
      mouseX: number,
      storyboardData: StoryboardMetadata,
    ): HoverPreviewState => {
      const relativeX = mouseX - progressRect.left;
      const fraction = relativeX / progressRect.width;
      const hoverTime = fraction * duration;

      // Find the appropriate tile
      const tile = storyboardData.tiles.find((t, i, arr) => {
        if (i === arr.length - 1) return true;
        return hoverTime >= t.start && hoverTime < arr[i + 1].start;
      });

      if (!tile) {
        return { ...initialHoverPreview };
      }

      // Position the preview appropriately
      const previewWidth = storyboardData.tile_width;
      const previewHeight = storyboardData.tile_height;
      let xPosition = relativeX;
      const halfPreviewWidth = previewWidth / 2;
      const minX = halfPreviewWidth;
      const maxX = progressRect.width - halfPreviewWidth;
      let isAtLeftEdge = false;
      let isAtRightEdge = false;

      if (xPosition < minX) {
        xPosition = minX;
        isAtLeftEdge = true;
      }
      if (xPosition > maxX) {
        xPosition = maxX;
        isAtRightEdge = true;
      }

      return {
        visible: true,
        x: xPosition,
        backgroundPosition: `-${tile.x}px -${tile.y}px`,
        width: previewWidth,
        height: previewHeight,
        hoverTime,
        isAtLeftEdge,
        isAtRightEdge,
      };
    },
    [duration],
  );

  // Handle timeline hover
  const handleTimelineHover = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement>,
      progressBarId: string = 'progress-bar-container', // Default value
    ) => {
      const progressBarContainer = document.getElementById(progressBarId);

      // If no progress bar container found, try to find it by class or data attribute
      const fallbackContainer =
        progressBarContainer ||
        document.querySelector('.MuiSlider-root') ||
        document.querySelector('[data-progress-bar]');

      // Check if hover is over progress bar
      if (!fallbackContainer || !fallbackContainer.contains(e.target as Node) || !storyboard) {
        if (hoverPreview.visible) {
          setHoverPreview((prev) => ({ ...prev, visible: false }));
        }
        return;
      }

      // Validate storyboard data
      if (!storyboard?.url || !storyboard.tiles?.length || duration <= 0) {
        return;
      }

      const progressRect = fallbackContainer.getBoundingClientRect();
      const newPreview = calculateHoverPreview(progressRect, e.clientX, storyboard);
      setHoverPreview(newPreview);
    },
    [storyboard, duration, hoverPreview.visible, calculateHoverPreview],
  );

  // Hide preview
  const hidePreview = useCallback(() => {
    setHoverPreview((prev) => ({ ...prev, visible: false }));
  }, []);

  return {
    storyboard,
    hoverPreview,
    setHoverPreview,
    fetchStoryboardMetadata,
    handleTimelineHover,
    hidePreview,
  };
};
